/**
 * Editor mentor overlay — Phase 5 runner.
 *
 * Resolves the current step definition, watches StepContext for
 * auto-pass / validate transitions, and renders the spotlight + card
 * (or modal) via a portal to <body>.
 *
 * Lifecycle, per step:
 *   1. enter      → emit step.entered (provider handled)
 *   2. on entry   → if autoPass(ctx) true → bypass + advance
 *   3. active     → measure target, render card
 *   4. on ctx Δ   → if validate(ctx) true → complete + advance
 *   5. primary    → run step.primaryAction (default: complete + advance)
 *   6. dismiss    → mentor.dismiss()
 *   7. last step  → markCompleted("finished_without_publish")
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOnboardingMentor } from "../OnboardingMentorProvider";
import { SpotlightRenderer } from "../spotlight/SpotlightRenderer";
import {
  scrollTargetIntoView,
  targetNeedsScrollIntoView,
} from "../spotlight/targetVisibility";
import { findTargetElement } from "../spotlight/targets";
import { useTargetRect } from "../spotlight/useTargetRect";
import { placeCard } from "../spotlight/positioning";
import { CoachingCard, WelcomeModal } from "../ui/CoachingCard";
import { ensureMentorStylesInjected } from "../ui/mentorStyles";
import { useReducedMotion } from "../ui/useReducedMotion";
import { emit, nowIso } from "../analytics/events";
import { STEP_BY_ID, nextStepId, STEP_ORDER } from "./steps/registry";
import type { StepContext } from "./types";

const CARD_MIN_WIDTH = 320;
const CARD_EST_HEIGHT = 160;
const TARGET_SCROLL_MARGIN_TOP = 96;
const TARGET_SCROLL_MARGIN_BOTTOM = 32;
const TARGET_SCROLL_RETRY_DELAYS_MS = [0, 120, 260, 520, 900, 1400, 2000];

interface EditorMentorOverlayProps {
  ctx: StepContext;
}

export function EditorMentorOverlay({ ctx }: EditorMentorOverlayProps) {
  const mentor = useOnboardingMentor();
  const session = mentor.session;
  const reducedMotion = useReducedMotion();

  // One-time CSS keyframe injection.
  useEffect(() => {
    ensureMentorStylesInjected();
  }, []);

  // Only render when we have an active session and the current step is
  // one of the Phase 5 steps. (Phase 6 will register e7–e10.)
  const def = session ? STEP_BY_ID[session.currentStep] : undefined;
  const isActive = Boolean(
    session && session.status === "active" && def !== undefined,
  );

  // Auto-pass + onEnter: once per step entry, fire any side-effect
  // onEnter() and then check if the page already satisfies the step.
  // We track which step we last entry-checked so this only fires once
  // per (sessionId, stepId).
  const bypassCheckedRef = useRef<{ session: string; step: string } | null>(
    null,
  );
  useEffect(() => {
    if (!isActive || !session || !def) return;
    const key = { session: session.sessionId, step: session.currentStep };
    const last = bypassCheckedRef.current;
    if (last?.session === key.session && last?.step === key.step) return;
    bypassCheckedRef.current = key;
    // onEnter side effect (e.g. e7_seo opens the SEO panel).
    try {
      def.onEnter?.(ctx);
    } catch {
      // never throw out of the runner
    }
    if (def.autoPass && def.autoPass(ctx)) {
      handleAdvance("bypassed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, session?.sessionId, session?.currentStep]);

  // Validation: on every ctx change, run validate. If it returns true,
  // complete + advance. Skip when no validator (manual-advance steps).
  const lastValidatedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isActive || !session || !def?.validate) return;
    if (lastValidatedRef.current === session.currentStep) {
      return;
    }
    if (def.validate(ctx)) {
      lastValidatedRef.current = session.currentStep;
      handleAdvance("completed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ctx.data,
    ctx.historyOpenedCount,
    ctx.isDirty,
    ctx.pagePublished,
    ctx.previewOpenedCount,
    ctx.showSeoPanel,
    ctx.showVersions,
    isActive,
    session?.currentStep,
  ]);

  // Reset the per-step refs when the step changes.
  useEffect(() => {
    lastValidatedRef.current = null;
  }, [session?.currentStep]);

  // ─── Target rect tracking ────────────────────────────────────────────
  const target = def?.target ?? null;
  const rectInfo = useTargetRect(target, isActive);
  const useModal =
    def?.placement === "modal-center" ||
    (def?.target !== null && rectInfo.status === "not_found");

  // When a step targets something lower in the page or inside the
  // scrollable component drawer, bring that element into view once as
  // soon as it resolves.
  const scrolledTargetRef = useRef<{ session: string; step: string } | null>(
    null,
  );
  useEffect(() => {
    if (
      !isActive ||
      !session ||
      !def?.target ||
      useModal ||
      rectInfo.status !== "ready" ||
      !rectInfo.element
    ) {
      return;
    }

    const key = { session: session.sessionId, step: session.currentStep };
    const last = scrolledTargetRef.current;
    if (last?.session === key.session && last?.step === key.step) return;
    scrolledTargetRef.current = key;
    const targetId = def.target;

    const timeouts = TARGET_SCROLL_RETRY_DELAYS_MS.map((delay, index) =>
      window.setTimeout(() => {
        const targetElement = findTargetElement(targetId);
        if (!targetElement) return;
        if (!document.contains(targetElement)) return;
        if (
          !targetNeedsScrollIntoView(targetElement, {
            viewportTopMargin: TARGET_SCROLL_MARGIN_TOP,
            viewportBottomMargin: TARGET_SCROLL_MARGIN_BOTTOM,
          })
        ) {
          return;
        }
        scrollTargetIntoView(targetElement, {
          viewportTopMargin: TARGET_SCROLL_MARGIN_TOP,
          viewportBottomMargin: TARGET_SCROLL_MARGIN_BOTTOM,
          behavior: reducedMotion || index > 0 ? "auto" : "smooth",
        });
      }, delay),
    );

    return () => {
      for (const timeout of timeouts) {
        window.clearTimeout(timeout);
      }
    };
  }, [
    def?.target,
    isActive,
    rectInfo.element,
    rectInfo.status,
    reducedMotion,
    session,
    useModal,
  ]);

  // When a target step has been "waiting" too long and ends in
  // not_found, emit telemetry and soften to a modal-style card so the
  // user isn't left staring at an empty scrim.
  useEffect(() => {
    if (!isActive || !session || !def?.target) return;
    if (rectInfo.status === "not_found") {
      emit({
        type: "target.not_found",
        sessionId: session.sessionId,
        stepId: session.currentStep,
        targetId: def.target,
        ts: nowIso(),
      });
    }
  }, [rectInfo.status, isActive, session, def]);

  // ─── Card positioning ────────────────────────────────────────────────
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardSize, setCardSize] = useState({
    w: CARD_MIN_WIDTH,
    h: CARD_EST_HEIGHT,
  });
  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    if (
      Math.abs(r.width - cardSize.w) > 1 ||
      Math.abs(r.height - cardSize.h) > 1
    ) {
      setCardSize({ w: r.width, h: r.height });
    }
  }, [def?.id, cardSize.w, cardSize.h]);

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 720,
  }));
  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ─── Handlers ────────────────────────────────────────────────────────
  function finishStep(reason: "completed" | "bypassed" | "skipped") {
    if (!session) return;
    const current = session.currentStep;
    mentor.completeStep(current, reason);
    const next = nextStepId(current);
    if (next) {
      mentor.advanceTo(next);
    } else {
      // End of the full journey. The outcome depends on whether the
      // page actually went live during the run.
      mentor.markCompleted(
        ctx.pagePublished ? "published" : "finished_without_publish",
      );
    }
  }

  function handleAdvance(reason: "completed" | "bypassed") {
    finishStep(reason);
  }

  function handleSkipStep() {
    finishStep("skipped");
  }

  function handlePrimary() {
    if (!session || !def) return;
    if (def.validate) {
      if (def.primaryAction) {
        void def.primaryAction(ctx);
      }
      // Validated steps only advance once the real page state matches.
      if (def.validate(ctx)) {
        handleAdvance("completed");
      }
      return;
    }
    if (def.primaryAction) {
      void def.primaryAction(ctx);
    }
    handleAdvance("completed");
  }

  function handleSecondary() {
    if (!session || !def) return;
    if (def.secondaryAction) {
      void def.secondaryAction(ctx);
      handleAdvance("completed");
      return;
    }
    if (def.tier === "soft") {
      handleSkipStep();
      return;
    }
    mentor.skip();
  }

  function handleDismiss() {
    mentor.dismiss();
  }

  function handleSkip() {
    handleSecondary();
  }

  // ─── Render ──────────────────────────────────────────────────────────
  const copy = useMemo(() => def?.copy(ctx) ?? null, [def, ctx]);

  const portalNode = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document.body;
  }, []);

  if (!isActive || !def || !copy || !portalNode) return null;

  const stepLabel = (() => {
    const total = STEP_ORDER.length;
    const idx = STEP_ORDER.indexOf(def.id) + 1;
    if (idx <= 0) return undefined;
    return `Step ${idx} of ${total}`;
  })();

  // Screen-reader live region — announces "Step X of Y: <title>" any
  // time the current step (or its localized title) changes. Polite so
  // it doesn't interrupt the user mid-action.
  const announcement = `${stepLabel ?? "Walkthrough"}: ${copy.title}`;
  const announcer = createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="staylayer-mentor-sr-only"
    >
      {announcement}
    </div>,
    portalNode,
  );

  const secondaryLabel =
    copy.secondaryLabel ??
    (def.tier === "soft" ? "Skip step" : "Skip walkthrough");

  if (useModal) {
    return (
      <>
        {announcer}
        {createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.55)",
              zIndex: 9998,
            }}
          >
            <WelcomeModal
              title={copy.title}
              body={
                <>
                  {copy.body}
                  {copy.hint ? (
                    <div className="mt-2 text-xs leading-relaxed text-slate-400">
                      {copy.hint}
                    </div>
                  ) : null}
                </>
              }
              primaryLabel={copy.primaryLabel}
              onPrimary={handlePrimary}
              secondaryLabel={secondaryLabel}
              onSecondary={handleSkip}
              onDismiss={handleDismiss}
            />
          </div>,
          portalNode,
        )}
      </>
    );
  }

  // Spotlight + side card path. While target is still resolving we
  // render only the scrim — no card flicker on the wrong position.
  const rect = rectInfo.rect;
  const placement = rect
    ? placeCard({
        target: rect,
        card: cardSize,
        viewport,
        preference: def.placement,
      })
    : null;

  return (
    <>
      {announcer}
      {createPortal(
        <SpotlightRenderer
          rect={rect}
          card={
            placement && rect ? (
              <div
                ref={cardRef}
                style={{
                  position: "absolute",
                  left: placement.left,
                  top: placement.top,
                  pointerEvents: "auto",
                }}
              >
                <CoachingCard
                  title={copy.title}
                  body={copy.body}
                  hint={copy.hint}
                  stepLabel={stepLabel}
                  primaryLabel={copy.primaryLabel}
                  onPrimary={handlePrimary}
                  secondaryLabel={secondaryLabel}
                  onSecondary={handleSkip}
                  onDismiss={handleDismiss}
                />
              </div>
            ) : null
          }
        />,
        portalNode,
      )}
    </>
  );
}
